import React from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Typography from '@mui/material/Typography';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowRightIcon from '@mui/icons-material/EastRounded';
import FacebookIcon from '@mui/icons-material/Facebook';
import VerticalDotsIcon from '@mui/icons-material/MoreVert';
import { ArchiveIcon } from 'lucide-react';

import type { IConversation } from 'src/api/landlord-messages-api';
import type { IConversationMatch } from 'src/services/api/messages/use-conversations-search';
import { type IProperty, EMessageChannel, type ILead } from 'src/services/api';
import { LeadUtils } from 'src/utils';
import { Flex, FlexColumn } from 'src/components/flex';
import TableCell from 'src/components/table/TableCell';
import { StageChip } from 'src/components/input/select-lead-stage/SelectLeadStage';
import { isConversation, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context';
import { type EConversationCategory } from 'src/pages/landlord/conversations/context/types';
import { useLead } from 'src/services/api/leads/use-lead';
import { theme } from 'src/theme/theme';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import { useOpenConversation } from 'src/pages/landlord/conversations/context/hooks/use-open-conversation';
import { useOpenConversationSearch } from 'src/pages/landlord/conversations/context/hooks/use-open-conversation-search';

import styles from 'src/pages/landlord/conversations/components/list/ConversationListItem.module.css';
import { type IUser } from 'src/api/users-api';

dayjs.extend(calendar);
dayjs.extend(utc);
dayjs.extend(timezone);

function ChannelIcon({ channel }: { channel: EMessageChannel }) {
    switch (channel) {
        case EMessageChannel.EMAIL:
            return <MailOutlineIcon color="action" sx={{ mb: '0 !important' }} />;
        case EMessageChannel.SMS:
            return <ChatBubbleOutlineIcon color="action" sx={{ mb: '-3px !important' }} />;
        case EMessageChannel.FACEBOOK_MESSENGER:
            return <FacebookIcon color="action" sx={{ mb: '0 !important' }} />;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _assertNever: never = channel;
        }
    }
}

type DesktopConversationListItemProps = {
    conversation: IConversation | IConversationMatchWithChannel;
    property?: IProperty;
    lead?: ILead;
    loggedInUser: IUser;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    leadsByUuid: Map<string, ILead>;
    focusedConversation?: IConversation | IConversationMatchWithChannel;
    setFocusedConversation?: (conversation: IConversation | undefined) => void;
    selectedConversations?: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations?: (conversations: Array<IConversation | IConversationMatchWithChannel>) => void;
    searchConversationsForNavigation?: (IConversation | IConversationMatchWithChannel)[];
}

export function DesktopConversationListItem({
    conversation,
    property,
    lead,
    loggedInUser,
    conversationsByCategory,
    leadsByUuid,
    setFocusedConversation,
    selectedConversations = [],
    setSelectedConversations = () => { },
    searchConversationsForNavigation,
}: DesktopConversationListItemProps) {
    // Use search-specific hook if searchConversationsForNavigation is provided, otherwise use regular hook
    const openConversationFromSearchHook = useOpenConversationSearch(
        leadsByUuid,
        searchConversationsForNavigation || []
    );
    const openConversationFromHook = useOpenConversation(conversationsByCategory, leadsByUuid);

    const openConversation = searchConversationsForNavigation ?
        openConversationFromSearchHook :
        openConversationFromHook;
    const { data: leadData, isLoading } = useLead(conversation.lead_uuid);
    const { data: teamMembers } = useTeamMembers();
    let channel = isConversation(conversation) ?
        conversation.latest_message_channel :
        conversation.msg_channel;

    if (!isLoading && leadData) {
        // prefer to use the RQ cached lead data if available
        lead = leadData;
    }

    if (!lead) {
        console.error('Lead not found for conversation, and failed to get lead data via network request', conversation);
        return null;
    }

    if (lead.ingestion_method === 'listing-site-email' && lead.phone_number) {
        channel = EMessageChannel.SMS;
    }

    const selected = selectedConversations.some((c) => c.lead_uuid === conversation.lead_uuid);

    const selectConversation = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (selected) {
            setSelectedConversations(selectedConversations.
                filter((c) => c.lead_uuid !== conversation.lead_uuid)
            );
        } else {
            setSelectedConversations([...selectedConversations, conversation]);
        }
    };

    let leadOwnerName = '';
    let owner = null;
    if (teamMembers) {
        if (lead?.owner === loggedInUser.id) {
            owner = loggedInUser;
            leadOwnerName = 'You';
        } else {
            owner = teamMembers.find((o) => o.id === lead?.owner);
            leadOwnerName = owner?.name || '';
        }
    }

    const hasUnreadMessages = isConversation(conversation) ? conversation.num_unread_messages > 0 : false;
    const totalTeamMembers = loggedInUser.leasing_team_members?.length || 1;
    const hasOtherTeamMembers = totalTeamMembers > 1;

    const firstContact = isConversation(conversation) ? formatDateTime(conversation.first_contact_at) : formatDateTime(lead?.inserted_at);
    const lastContact = isConversation(conversation) && conversation.last_contact_at ? formatDateTime(conversation.last_contact_at) : undefined;
    const checkboxDisplay = selectedConversations.length > 0 ? 'block !important' : 'none';

    return (
        <>
            <TableRow
                data-testid="conversation-row"
                onMouseEnter={() => setFocusedConversation?.(conversation as IConversation)}
                onClick={() => openConversation(conversation, window.location.pathname + window.location.search)}
                className={clsx(styles.tableRow, hasUnreadMessages && styles.hasUnreadMessages)}
            >
                <TableCell
                    width={40}
                    className={styles.tableCellWithCheckbox}
                    data-testid="checkbox-column"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Checkbox
                        className={styles.checkbox}
                        sx={{
                            display: checkboxDisplay,
                        }}
                        checked={selected}
                        onClick={selectConversation}
                    />
                </TableCell>

                <TableCell width="40%" className={clsx(styles.textWithEllipsis, styles.leadNameTableCell)} data-testid="lead-name-column">
                    <Typography>{LeadUtils.getLeadName(lead)}</Typography>
                </TableCell>

                <TableCell width="20%" className={clsx(styles.textWithEllipsis, styles.leadNameTableCell)} data-testid="stage-column">
                    {lead?.stage && <StageChip value={lead.stage} clickable={false} />}
                </TableCell>

                <TableCell width="50%" className={styles.textWithEllipsis} data-testid="message-preview-column">
                    <Flex alignItems="center">
                        <ChannelIcon channel={channel} />
                        <Typography>
                            {isConversation(conversation) ?
                                conversation.latest_message_contents :
                                (conversation as IConversationMatch).matching_message_contents
                            }
                        </Typography>
                    </Flex>
                </TableCell>

                <TableCell width="20%" className={styles.textWithEllipsis} data-testid="property-name-column">
                    <Typography variant="body2">
                        {property?.name || 'Property not set'}
                    </Typography>
                </TableCell>

                {hasOtherTeamMembers && (
                    <TableCell width="20%" data-testid="lead-owner-column">
                        <Flex alignItems="center" columnGap={1}>
                            <ProfileImageRounded url={owner?.profile_photo_url} width={20} height={20} showEmptyImage userName={owner?.name} />
                            <Typography className={styles.textWithEllipsis}>{leadOwnerName}</Typography>
                        </Flex>
                    </TableCell>
                )}

                <TableCell width={18} sx={{ margin: 0, padding: 0 }}>
                    {
                        lead.is_archived && <ArchiveIcon color={theme.palette.secondary.main} size={18} />
                    }
                </TableCell>

                <TableCell width={200} align="right" className={styles.textWithEllipsis} data-testid="date-column">
                    <Flex justifyContent="flex-end" alignItems="center" columnGap={1}>
                        <Typography variant="body2">{firstContact}</Typography>
                        {lastContact && firstContact !== lastContact && (
                            <>
                                <ArrowRightIcon fontSize="small" />
                                <Typography variant="body2">{lastContact}</Typography>
                            </>
                        )}
                    </Flex>
                </TableCell>

                <TableCell width={40} data-testid="unread-indicator-column">
                    {hasUnreadMessages && <CircleIcon color="primary" className={styles.unreadIndicator} />}
                </TableCell>
            </TableRow>
        </>
    );
}

type MobileConversationListItemProps = {
    conversation: IConversation | IConversationMatchWithChannel;
    property?: IProperty;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    leadsByUuid: Map<string, ILead>;
    setMobileMenuAnchorEl: (anchorEl: HTMLElement) => void;
    setSelectedConversations: (conversations: Array<IConversation | IConversationMatchWithChannel>) => void;
    searchConversationsForNavigation?: (IConversation | IConversationMatchWithChannel)[];
}

export function MobileConversationListItem({
    conversation,
    property,
    conversationsByCategory,
    leadsByUuid,
    setMobileMenuAnchorEl,
    setSelectedConversations,
    searchConversationsForNavigation
}: MobileConversationListItemProps) {
    // Use search-specific hook if searchConversationsForNavigation is provided, otherwise use regular hook
    const openConversationFromSearchHook = useOpenConversationSearch(
        leadsByUuid,
        searchConversationsForNavigation || []
    );
    const openConversationFromHook = useOpenConversation(conversationsByCategory, leadsByUuid);

    const openConversation = searchConversationsForNavigation ?
        openConversationFromSearchHook :
        openConversationFromHook;

    const channel = 'latest_message_channel' in conversation ? conversation.latest_message_channel : conversation.msg_channel;
    // read the lead from the RQ cache
    // this ensures we have the latest data after any updates
    const { data: lead } = useLead(conversation.lead_uuid);

    const hasUnreadMessages = 'num_unread_messages' in conversation ? conversation.num_unread_messages > 0 : false;

    const firstContact = 'first_contact_at' in conversation ? formatDateTime(conversation?.first_contact_at) : formatDateTime(conversation.matching_message_inserted_at);
    const lastContact = 'last_contact_at' in conversation ? formatDateTime(conversation?.last_contact_at) : null;

    const latestMessageContents = 'latest_message_contents' in conversation ? conversation.latest_message_contents : conversation.matching_message_contents;

    return (
        <>
            <FlexColumn
                className={clsx(styles.mobileListItem, hasUnreadMessages && styles.hasUnreadMessages)}
                onClick={() => openConversation(conversation, window.location.pathname + window.location.search)}
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" width={300}>
                        <ChannelIcon channel={channel} />
                        {lead?.stage && <StageChip value={lead.stage} clickable={false} />}
                        <Typography className="ellipsisText">{LeadUtils.getLeadName(lead)}</Typography>
                    </Flex>

                    <IconButton
                        onClick={(event) => {
                            event.stopPropagation();
                            setSelectedConversations([conversation]);
                            setMobileMenuAnchorEl(event.currentTarget);
                        }}
                    >
                        <VerticalDotsIcon />
                    </IconButton>
                </Flex>


                <Flex alignItems="center" columnGap={1}>
                    <Typography variant="body2">{firstContact}</Typography>
                    {firstContact !== lastContact && (
                        <>
                            <ArrowRightIcon fontSize="small" />
                            <Typography variant="body2">{lastContact}</Typography>
                        </>
                    )}
                </Flex>

                <Typography className={styles.textWithEllipsis}>
                    {latestMessageContents}
                </Typography>

                <Typography className={styles.textWithEllipsis} variant="body2">
                    {property?.name || 'Property not set'}
                </Typography>
            </FlexColumn>
        </>
    );
}

const formatDateTime = (date: string) => {
    return dayjs.utc(date).tz().calendar(null, {
        sameDay: 'h:mm A',
        lastDay: 'MMM D',
        lastWeek: 'MMM D',
        sameElse: 'MMM D'
    });
};
